import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useLang, useT, pickLocalized } from '../i18n';
import { detailComponents } from '../works/registry';
import type { Work } from '../types/work';

type Status = 'loading' | 'ready' | 'not-found' | 'error';

interface WorkDetailProps {
  id: string;
  onBack: () => void;
}

export default function WorkDetail({ id, onBack }: WorkDetailProps) {
  const t = useT();
  const { lang } = useLang();
  const [status, setStatus] = useState<Status>('loading');
  const [work, setWork] = useState<Work | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;
    api
      .works()
      .then((data) => {
        if (cancelled) return;
        const found = data.find((w) => w.id === id);
        if (!found) {
          setStatus('not-found');
          return;
        }
        setWork(found);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'unknown error');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const Detail = detailComponents[id];

  return (
    <article className="flex max-w-3xl flex-col gap-8">
      <button
        type="button"
        onClick={onBack}
        className="self-start rounded-full px-3 py-1.5 font-mono text-xs text-coffee transition hover:bg-neutral-100 hover:text-cocoa focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
      >
        {t('works.detail.back')}
      </button>

      {status === 'loading' && (
        <p className="text-coffee">{t('works.loading')}</p>
      )}
      {status === 'error' && (
        <p className="text-cocoa font-mono text-sm italic">
          {t('works.error', { error })}
        </p>
      )}
      {status === 'not-found' && (
        <p className="text-cocoa font-mono text-sm italic">
          {t('works.detail.notFound')}
        </p>
      )}

      {status === 'ready' && work && (
        <>
          <header className="flex flex-col gap-4 rounded-xl border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float md:p-8">
            <div className="flex items-center gap-3 text-xs font-mono text-coffee/70">
              <span className="text-coffee">{t(`works.category.${work.kind}`)}</span>
              <span aria-hidden className="text-neutral-300">·</span>
              <span>{work.year}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold leading-tight text-cocoa">
              {pickLocalized(work.title, lang)}
            </h1>
            <p className="text-lg text-coffee leading-relaxed">
              {pickLocalized(work.summary, lang)}
            </p>
            {work.tags.length > 0 && (
              <ul className="flex flex-wrap gap-1.5">
                {work.tags.map((tag) => (
                  <li
                    key={tag}
                    className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-coffee/80"
                  >
                    {tag}
                  </li>
                ))}
              </ul>
            )}
          </header>

          {Detail ? (
            <div className="prose prose-cocoa max-w-none rounded-xl border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float md:p-8">
              <Detail />
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border border-neutral-200 bg-white p-6 shadow-soft transition-shadow duration-200 hover:shadow-float">
              <p className="font-mono text-sm text-coffee">
                {t('works.detail.placeholder')}
              </p>
              <button
                type="button"
                onClick={onBack}
                className="self-start rounded-full bg-tan px-4 py-2 font-mono text-sm text-neutral-100 shadow-soft transition hover:-translate-y-0.5 hover:bg-neutral-800 hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-950/15"
              >
                {t('works.detail.backCta')}
              </button>
            </div>
          )}
        </>
      )}
    </article>
  );
}
